import { useState } from "react";
import { PersonalInfoSection } from "./PersonalInfoSection";
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../graphql/client';
import { Download } from 'lucide-react';
import { useGetDatevDocumentsByUser } from '../graphql/hooks/useAdminDashboard';
import { MonthPicker } from './core/Monthpicker';
import { useAuth } from '../graphql/hooks/useAuth';
import { useDocumentsForUser } from '../graphql/hooks/useAdminDashboard';
import toast from 'react-hot-toast';
import { UserDocumentPanel } from './UserDocumentPanel';

type Props = {
  userData: any;
};

type MainTab = "personal-info" | "documents" | "my-documents";
type FileTypeFilter = 'all' | 'zip' | 'pdf';

export function UserProfileContent({ userData }: Props) {
  const { token } = useAuth();
  const [activeMainTab, setActiveMainTab] = useState<MainTab>("personal-info");
  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>('all');
  const [filterMode, setFilterMode] = useState<'single' | 'range'>('single');
  const [singleMonth, setSingleMonth] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');

  const { data: datevDocuments } = useGetDatevDocumentsByUser(
    100,
    userData?.id ? Number(userData.id) : undefined
  );

  const filteredDocs = (datevDocuments?.data ?? []).filter((doc) => {
    if (fileTypeFilter !== 'all' && doc.file_type !== fileTypeFilter) return false;
    if (filterMode === 'single') {
      if (singleMonth && doc.month !== singleMonth) return false;
    } else {
      if (rangeFrom && doc.month < rangeFrom) return false;
      if (rangeTo && doc.month > rangeTo) return false;
    }
    return true;
  });

const mainTabs = [
    { id: "personal-info" as MainTab, label: "Persönliche Informationen" },
    { id: "documents" as MainTab, label: "Lohnbüro" },
    { id: "my-documents" as MainTab, label: "Dokumente" },
  ];

  const getInitials = (name: string) => {
    const parts = name.split(" ");
    if (parts.length >= 2) return parts[0][0] + parts[1][0];
    return name.substring(0, 2);
  };

  if (!userData) return null;
  const { data: documentsSorted = [] } = useDocumentsForUser(userData?.id ?? '');

  return (
    <div className="bg-[#f5f5f5]">
      {/* Header */}
      <div className="bg-white border-b border-[#e9e9e9]">
        <div className="px-4 py-4 flex items-center gap-4">
          <div className="w-20 h-20 bg-gradient-to-br from-[#6b3fa0] to-[#9b59b6] rounded-full flex items-center justify-center text-white text-[24px] font-bold shadow-lg">
            {getInitials(userData.full_name || "")}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-[#22c55e]/10 text-[#22c55e] text-[12px] font-medium rounded-full">
                {userData.active ? "Aktiv" : "Inaktiv"}
              </span>
              {userData.job_title && (
                <span className="px-3 py-1 bg-[#0084c7]/10 text-[#0084c7] text-[12px] font-medium rounded-full">
                  {userData.job_title}
                </span>
              )}
              {userData.user_type?.type === "admin" && (
                <span className="px-3 py-1 bg-[#f59e0b]/10 text-[#f59e0b] text-[12px] font-medium rounded-full">
                  Administrator
                </span>
              )}
            </div>
            <div className="text-[20px] font-semibold">
              {userData.first_name} {userData.last_name}
            </div>
          </div>
        </div>

        <div className="px-6">
          <div className="flex gap-6 border-b border-[#e9e9e9]">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveMainTab(tab.id)}
                className={`pb-4 text-[14px] font-medium transition-colors relative ${
                  activeMainTab === tab.id ? "text-[#0084c7]" : "text-[#6b6b6b] hover:text-[#1c1c1c]"
                }`}
              >
                {tab.label}
                {activeMainTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0084c7]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeMainTab === "personal-info" && (
        <div className="px-6 py-6">
          <div className="bg-white rounded-[12px] border border-[#e9e9e9] p-6 md:p-8">
            <PersonalInfoSection userData={userData} isEditing={false} />
          </div>
        </div>
      )}

      {activeMainTab === "documents" && (
        <div className="px-6 py-6">
          <div className="bg-white rounded-[12px] border border-[#e9e9e9] p-6 space-y-4">

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1 bg-[#f5f5f5] rounded-lg p-1">
                {(['all', 'pdf', 'zip'] as FileTypeFilter[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setFileTypeFilter(type)}
                    className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                      fileTypeFilter === type
                        ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                        : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                    }`}
                  >
                    {type === 'all' ? 'Alle' : type.toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-[#f5f5f5] rounded-lg p-1">
                <button
                  onClick={() => setFilterMode('single')}
                  className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                    filterMode === 'single'
                      ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                      : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                  }`}
                >
                  Monat
                </button>
                <button
                  onClick={() => setFilterMode('range')}
                  className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                    filterMode === 'range'
                      ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                      : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                  }`}
                >
                  Zeitraum
                </button>
              </div>

              {filterMode === 'single' ? (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-[#6b6b6b]">Monat:</span>
                  <MonthPicker value={singleMonth} onChange={setSingleMonth} placeholder="Alle Monate" />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-[#6b6b6b]">Von:</span>
                  <MonthPicker value={rangeFrom} onChange={setRangeFrom} placeholder="Von" />
                  <span className="text-[13px] text-[#6b6b6b]">Bis:</span>
                  <MonthPicker value={rangeTo} onChange={setRangeTo} placeholder="Bis" />
                </div>
              )}
            </div>

            {/* Table */}
            {filteredDocs.length > 0 ? (
              <div>
                <div className="grid grid-cols-[100px_1fr_120px_150px] gap-4 px-4 py-3 mb-2 bg-[#f5f5f5] rounded-lg text-[12px] font-medium text-[#6b6b6b]">
                  <div>ID</div>
                  <div>Pfad</div>
                  <div>Monat</div>
                  <div>Aktion</div>
                </div>

                {filteredDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="grid grid-cols-[100px_1fr_120px_150px] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors border-b border-[#e9e9e9]"
                  >
                    <div className="text-[#6b6b6b]">{doc.id}</div>
                    <div className="text-[#6b6b6b] truncate">{doc.path}</div>
                    <div className="text-[#6b6b6b]">{doc.month}</div>
                    <div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(
                              `${DEFAULT_ENDPOINT_URL_EXPORT}/datev/download/${doc.id}`,
                              {
                                method: 'GET',
                                headers: { Authorization: token },
                              }
                            );

                            if (!response.ok) {
                              toast.error('Dokument konnte nicht geöffnet werden.');
                              return;
                            }

                            const blob = await response.blob();

                            if (doc.file_type === 'pdf') {
                              const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                              const pdfUrl = window.URL.createObjectURL(pdfBlob);
                              window.open(pdfUrl, '_blank');
                            } else {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.path.split('/').pop() ?? `datev_${doc.id}.zip`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-white bg-[#0084c7] hover:bg-[#006fa8] rounded-lg transition-colors"
                      >
                        <Download className="size-4" />
                        {doc.file_type === 'pdf' ? 'Vorschau' : 'Herunterladen'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="text-[#6b6b6b] text-[16px]">Keine Dokumente vorhanden</p>
              </div>
            )}
          </div>
        </div>
      )}
      {activeMainTab === "my-documents" && (
        <div className="px-6 py-6">
          <div className="bg-white rounded-[12px] border border-[#e9e9e9] p-6 space-y-4">
            {documentsSorted.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-[#6b6b6b] text-[16px]">Keine Dokumente vorhanden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentsSorted.map(({ documentCategory, documents }) => (
                  <UserDocumentPanel
                    key={documentCategory.id}
                    categoryName={documentCategory.name}
                    documents={documents}
                    token={token}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}